import { useEffect, useRef } from 'react';
import { useLeafletContext } from '@react-leaflet/core';
import { useMap } from 'react-leaflet';
import parseGeoraster from 'georaster';
import GeoRasterBaseLayer, {
  GeoRaster,
  GeoRasterLayerOptions,
} from 'georaster-layer-for-leaflet';
import chroma from 'chroma-js';
import { Coloring, ColoringType } from '../../api/api.ts';

const {
  // VITE_MAPBOX_USERNAME: MAPBOX_USERNAME,
  // VITE_MAPBOX_STYLE_ID: MAPBOX_STYLE_ID,
  // VITE_MAPBOX_ACCESS_TOKEN: MAPBOX_ACCESS_TOKEN,
  VITE_BACKEND_BASE_URL: BACKEND_BASE_URL,
} = import.meta.env;

export function GeoRasterLayer({
  url,
  coloring,
}: {
  url: string;
  coloring: Coloring;
}) {
  const geoTiffLayerRef = useRef();
  const context = useLeafletContext();
  const map = useMap();

  useEffect(() => {
    const aborter = new AbortController();
    const container = context.layerContainer || context.map;

    fetch(BACKEND_BASE_URL + url, { signal: aborter.signal })
      .then((response) => response.blob())
      .then((blob) => {
        parseGeoraster(blob).then((georaster: GeoRaster) => {
          const min = georaster.mins[0];
          const range = georaster.ranges[0];

          const options: GeoRasterLayerOptions = {
            resolution: 512,
            opacity: 0.5,
            georaster: georaster,
          };

          if (coloring.type === ColoringType.Scale) {
            const scale = chroma.scale(coloring.colors).domain([1, 0]);
            options.pixelValuesToColorFn = (
              pixelValues: [number, number, number]
            ): string => {
              const pixelValue = pixelValues[0];
              if (pixelValue === 0) return 'rgba(0,0,0,0)';
              const scaledPixelValue = (pixelValue - min) / range;
              return scale(scaledPixelValue).hex();
            };
          } else {
            console.warn(
              `GeoRasterLayer coloring only implemented for coloring type ${coloring.type}`
            );
          }

          if (geoTiffLayerRef.current) return; // Prevent duplicate loading of layers
          geoTiffLayerRef.current = new GeoRasterBaseLayer(options);
          container.addLayer(geoTiffLayerRef.current as GeoRaster);
        });
      })
      .catch((e) => {
        if (e === 'component destructor') return; // fail gracefully on signal abort
        alert('Es gab ein Problem beim Laden der Daten');
        console.info(e);
      });
    return () => {
      if (geoTiffLayerRef.current && map) {
        map.removeLayer(geoTiffLayerRef.current);
      }
      aborter.abort('component destructor');
    };
  }, [url]);

  return null;
}
